// Suppliers → Discount sheet: reading a supplier's discount sheet CSV.
//
// Every supplier lays its sheet out differently, so nothing here knows about
// TKC or PWS. The columns are GUESSED from the header names and the retailer
// confirms (or changes) the guess before anything is sent. Today's two:
//   TKC  Discount Code, Product Range, Discount Percentage, Valid From, Valid Until
//   PWS  Door Range Description, Discount Code, Product Type, Discount Percentage

export type SheetField = "discountCode" | "description" | "productType" | "discountPercent" | "validFrom" | "validUntil";

export const SHEET_FIELDS: ReadonlyArray<{ key: SheetField; label: string; required: boolean }> = [
  { key: "description", label: "Range / description", required: true },
  { key: "discountPercent", label: "Discount %", required: true },
  { key: "discountCode", label: "Discount code", required: false },
  { key: "productType", label: "Product type", required: false },
  { key: "validFrom", label: "Valid from", required: false },
  { key: "validUntil", label: "Valid until", required: false },
];

/** RFC 4180-ish: quoted fields, doubled quotes, CRLF or LF, a BOM. */
export function parseCsv(text: string): string[][] {
  const s = text.replace(/^﻿/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && s[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}

const GUESS: Record<SheetField, RegExp[]> = {
  discountCode: [/discount\s*code/i, /^code$/i, /\bcode\b/i],
  description: [/range\s*description/i, /product\s*range/i, /description/i, /\brange\b/i, /^name$/i],
  productType: [/product\s*type/i, /^type$/i],
  discountPercent: [/discount\s*(percent|percentage|%)/i, /percent/i, /%/, /^discount$/i],
  validFrom: [/valid\s*from/i, /start/i, /^from$/i],
  validUntil: [/valid\s*(until|to)/i, /expir/i, /^(until|to)$/i, /\bend\b/i],
};

/** Header index per field, or -1. Each header is used at most once. */
export function guessColumns(headers: readonly string[]): Record<SheetField, number> {
  const taken = new Set<number>();
  const out = {} as Record<SheetField, number>;
  // Most specific fields first, so "Discount Code" is not read as the % column.
  for (const f of ["discountCode", "productType", "validFrom", "validUntil", "discountPercent", "description"] as SheetField[]) {
    out[f] = -1;
    for (const rx of GUESS[f]) {
      const i = headers.findIndex((h, j) => !taken.has(j) && rx.test(h.trim()));
      if (i >= 0) { out[f] = i; taken.add(i); break; }
    }
  }
  return out;
}

export type SheetRow = Partial<Record<SheetField, string>>;

export function rowsFromCsv(table: readonly string[][], cols: Record<SheetField, number>): SheetRow[] {
  return table.slice(1).map((cells) => {
    const r: SheetRow = {};
    for (const f of Object.keys(cols) as SheetField[]) {
      const i = cols[f];
      if (i >= 0) r[f] = (cells[i] ?? "").trim();
    }
    return r;
  });
}
